import { api } from '@/lib/api/client'
import type { Visibility } from '@/types/domain'
import { fetchAll } from '@/lib/api/paged'

/**
 * A file that belongs to the project rather than to a task.
 *
 * The same shape the API uses for a file on a work item — a document is one
 * with no `workItemId` — so nothing here has to know which of the two it is
 * holding. `name` is what to show; `fileName` is what was uploaded.
 */
export interface ProjectDocument {
  id: string
  name: string
  fileName: string
  title: string | null
  mimeType: string
  sizeBytes: number
  visibility: Visibility
  workItemId: string | null
  projectId: string
  commentCount?: number
  createdAt: string
  uploadedBy?: { id: string; name: string; color: string | null } | null
}

export interface DocumentComment {
  id: string
  body: string
  visibility: Visibility
  attachmentId: string | null
  createdAt: string | null
  edited: boolean
  author?: { id: string; name: string; color: string | null } | null
}

/** What the server refused, and why, so the drop can be reported honestly. */
export interface RejectedFile {
  name: string
  reason: string
}

export interface UploadResult {
  stored: ProjectDocument[]
  rejected: RejectedFile[]
}

export async function fetchDocuments(projectKey: string): Promise<ProjectDocument[]> {
  return fetchAll<ProjectDocument>(`/projects/${projectKey}/documents`)
}

/**
 * One request for the whole drop.
 *
 * The response is deliberately not all-or-nothing: files the server accepted
 * come back in `stored` and the ones it would not take come back named in
 * `rejected`, so nineteen good files are not lost to one bad one.
 */
export async function uploadDocuments(
  projectKey: string,
  files: File[],
  visibility: Visibility,
): Promise<UploadResult> {
  const form = new FormData()
  files.forEach((file) => form.append('files[]', file))
  form.append('visibility', visibility)

  const { data } = await api.post<{
    data: ProjectDocument[]
    meta: { created: number; rejected: RejectedFile[] }
  }>(`/projects/${projectKey}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return { stored: data.data, rejected: data.meta.rejected }
}

export async function updateDocument(
  id: string,
  changes: { title?: string | null; visibility?: Visibility },
): Promise<ProjectDocument> {
  const { data } = await api.patch<{ data: ProjectDocument }>(`/documents/${id}`, changes)

  return data.data
}

export async function deleteDocument(id: string): Promise<void> {
  await api.delete(`/attachments/${id}`)
}

/**
 * The bytes are never served by the API. This asks for a signed link that
 * expires in minutes and hands it straight to the browser.
 */
export async function documentUrl(id: string): Promise<string> {
  const { data } = await api.get<{ data: { url: string } }>(`/attachments/${id}/url`)

  return data.data.url
}

export async function fetchDocumentComments(id: string): Promise<DocumentComment[]> {
  return fetchAll<DocumentComment>(`/documents/${id}/comments`)
}

export async function postDocumentComment(
  id: string,
  input: { body: string; visibility: Visibility },
): Promise<DocumentComment> {
  const { data } = await api.post<{ data: DocumentComment }>(`/documents/${id}/comments`, input)

  return data.data
}

/** Files attached to one work item, as opposed to the project's own library. */
export async function fetchItemAttachments(workItemId: string): Promise<ProjectDocument[]> {
  return fetchAll<ProjectDocument>(`/work-items/${workItemId}/attachments`)
}

export async function uploadItemAttachment(
  workItemId: string,
  file: File,
  visibility: Visibility,
): Promise<ProjectDocument> {
  const form = new FormData()
  form.append('file', file)
  form.append('visibility', visibility)

  const { data } = await api.post<{ data: ProjectDocument }>(
    `/work-items/${workItemId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  )

  return data.data
}
